import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Save, Users, Trash2 } from "lucide-react";
import { CLASS_COLORS, WOW_CLASSES } from "@/lib/constants";
import { bulkClassUpdate, deletePlayerApi } from "@/lib/api";
import { toast } from "sonner";

// Filter out entries that clearly aren't player names
function isValidPlayerName(name) {
  if (!name || name.length < 2 || name.length > 30) return false;
  // Names that are just numbers, or contain brackets/special formatting
  if (/^\d+$/.test(name)) return false;
  if (/[\[\]{}()=<>]/.test(name)) return false;
  // Common non-player strings from sheet parsing
  const bad = [
    "general", "tank", "healer", "dps", "total", "average", "metric", "boss",
    "trash", "consumables", "engineering", "trinkets", "stats", "damage",
    "interrupted", "melee", "ranged", "unknown", "warrior", "paladin",
    "hunter", "rogue", "priest", "shaman", "mage", "warlock", "druid",
    "warriors", "paladins", "hunters", "rogues", "priests", "shamans",
    "mages", "warlocks", "druids",
  ];
  if (bad.includes(name.toLowerCase())) return false;
  // Likely a header row if it contains spaces and common words
  if (/^(# of|total |raw |avoidable )/i.test(name)) return false;
  return true;
}

export default function BulkClassAssignment({ players, onUpdated }) {
  const [assignments, setAssignments] = useState({});
  const [removed, setRemoved] = useState(new Set());
  const [open, setOpen] = useState(false);

  const validPlayers = players.filter((p) => isValidPlayerName(p.name));
  const invalidPlayers = players.filter((p) => !isValidPlayerName(p.name));

  useEffect(() => {
    const init = {};
    players.forEach((p) => { init[p.id] = p.wow_class; });
    setAssignments(init);
    setRemoved(new Set());
  }, [players, open]);

  const handleSave = async () => {
    // First delete removed players
    for (const pid of removed) {
      try {
        await deletePlayerApi(pid);
      } catch (e) {
        console.error("Failed to delete player", pid);
      }
    }

    const updates = Object.entries(assignments)
      .filter(([pid, cls]) => {
        if (removed.has(pid)) return false;
        const player = players.find((p) => p.id === pid);
        return player && player.wow_class !== cls;
      })
      .map(([pid, cls]) => ({ player_id: pid, wow_class: cls }));

    if (updates.length === 0 && removed.size === 0) {
      toast.info("No changes to save");
      return;
    }

    if (updates.length > 0) {
      try {
        const res = await bulkClassUpdate(updates);
        toast.success(`Updated ${res.updated} player(s)${removed.size > 0 ? `, removed ${removed.size}` : ""}`);
      } catch (e) {
        toast.error("Failed to update classes");
      }
    } else if (removed.size > 0) {
      toast.success(`Removed ${removed.size} player(s)`);
    }

    onUpdated();
    setOpen(false);
  };

  const toggleRemove = (pid) => {
    setRemoved((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  };

  const unknownCount = validPlayers.filter((p) => p.wow_class === "Unknown").length;
  const displayPlayers = validPlayers.filter((p) => !removed.has(p.id));
  const removedPlayers = [...validPlayers.filter((p) => removed.has(p.id)), ...invalidPlayers];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="bulk-class-btn" variant="ghost" size="sm"
          className="w-full justify-start gap-2 text-xs text-muted-foreground hover:text-foreground rounded-sm h-7 px-3">
          <Users className="h-3.5 w-3.5" />
          Bulk Assign Classes
          {unknownCount > 0 && (
            <Badge variant="destructive" className="ml-auto text-[10px] px-1 py-0 h-4">{unknownCount}</Badge>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[80vh] bg-background">
        <DialogHeader>
          <DialogTitle className="heading text-xl uppercase tracking-tight">Assign Player Classes</DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[60vh] pr-2">
          <div className="space-y-1">
            {displayPlayers.map((p) => (
              <div key={p.id} data-testid={`bulk-class-${p.name}`}
                className="flex items-center gap-3 py-1.5 px-2 hover:bg-card/50 rounded-sm">
                <div className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: CLASS_COLORS[assignments[p.id]] || CLASS_COLORS.Unknown }} />
                <span className="text-sm font-medium flex-1 truncate"
                  style={{ color: CLASS_COLORS[assignments[p.id]] || CLASS_COLORS.Unknown }}>
                  {p.name}
                </span>
                <Select value={assignments[p.id] || "Unknown"}
                  onValueChange={(v) => setAssignments((prev) => ({ ...prev, [p.id]: v }))}>
                  <SelectTrigger className="h-7 w-28 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WOW_CLASSES.map((c) => (
                      <SelectItem key={c} value={c}>
                        <span style={{ color: CLASS_COLORS[c] }}>{c}</span>
                      </SelectItem>
                    ))}
                    <SelectItem value="Unknown"><span className="text-muted-foreground">Unknown</span></SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0"
                  data-testid={`bulk-remove-${p.name}`}
                  onClick={() => toggleRemove(p.id)}>
                  <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                </Button>
              </div>
            ))}

            {/* Show removed/invalid entries */}
            {removedPlayers.length > 0 && (
              <>
                <div className="pt-3 pb-1 px-2">
                  <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                    Queued for Removal ({removedPlayers.length})
                  </span>
                </div>
                {removedPlayers.map((p) => (
                  <div key={p.id} data-testid={`bulk-removed-${p.name}`}
                    className="flex items-center gap-3 py-1.5 px-2 rounded-sm opacity-50">
                    <div className="w-2 h-2 rounded-full flex-shrink-0 bg-destructive" />
                    <span className="text-sm font-medium flex-1 truncate line-through text-muted-foreground">
                      {p.name}
                    </span>
                    {isValidPlayerName(p.name) && (
                      <Button variant="ghost" size="sm" className="h-6 text-xs text-muted-foreground"
                        onClick={() => toggleRemove(p.id)}>
                        Undo
                      </Button>
                    )}
                    {!isValidPlayerName(p.name) && (
                      <Badge variant="outline" className="text-[10px] text-destructive border-destructive/30">Invalid</Badge>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </ScrollArea>
        <div className="flex justify-between items-center mt-2">
          <span className="text-xs text-muted-foreground">
            {displayPlayers.length} player(s){removed.size > 0 ? `, ${removed.size} to remove` : ""}
          </span>
          <div className="flex gap-2">
            <DialogClose asChild>
              <Button variant="outline" size="sm" className="rounded-sm text-xs">Cancel</Button>
            </DialogClose>
            <Button data-testid="bulk-class-save-btn" size="sm" onClick={handleSave}
              className="rounded-sm font-bold uppercase tracking-wider text-xs bg-foreground text-background">
              <Save className="h-3.5 w-3.5 mr-1" /> Save All
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
